/**
 * SurveyActivityFeed - Shows recent survey-related activity on the D&A Dashboard.
 * Auto-updates in real-time via Firestore onSnapshot listener.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Skeleton,
  Button,
} from '@mui/material';
import {
  Assignment as SurveyIcon,
  CheckCircle as CompleteIcon,
  PlayArrow as ActiveIcon,
  Stop as ClosedIcon,
  NoteAdd as CreatedIcon,
  Send as ResponseIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserOrganizations } from '../hooks/useOrganization';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { collections } from '../../../utils/firestore';

// Map action types to icons and labels
const ACTION_CONFIG = {
  survey_created: { icon: <CreatedIcon fontSize="small" />, label: 'Survey Created', color: 'info' },
  survey_activated: { icon: <ActiveIcon fontSize="small" />, label: 'Survey Activated', color: 'success' },
  survey_closed: { icon: <ClosedIcon fontSize="small" />, label: 'Survey Closed', color: 'default' },
  survey_completed: { icon: <CompleteIcon fontSize="small" />, label: 'Response Submitted', color: 'success' },
  survey_response: { icon: <ResponseIcon fontSize="small" />, label: 'Response Submitted', color: 'primary' },
  survey_status_changed: { icon: <HistoryIcon fontSize="small" />, label: 'Status Changed', color: 'warning' },
  // OE Conditions (P2-16) — logged by trlService
  condition_created: { icon: <CreatedIcon fontSize="small" />, label: 'OE Condition Logged', color: 'warning' },
  condition_l1_opened: { icon: <HistoryIcon fontSize="small" />, label: 'L1 Review Started', color: 'info' },
  condition_l1_approved: { icon: <ActiveIcon fontSize="small" />, label: 'L1 Approved', color: 'info' },
  condition_l2_approved: { icon: <ActiveIcon fontSize="small" />, label: 'Approved/Active', color: 'success' },
  condition_returned: { icon: <HistoryIcon fontSize="small" />, label: 'Returned to L1', color: 'warning' },
  condition_not_approved: { icon: <ClosedIcon fontSize="small" />, label: 'Not Approved', color: 'error' },
  condition_owner_changed: { icon: <HistoryIcon fontSize="small" />, label: 'Owner Changed', color: 'default' },
  condition_resolution_submitted: { icon: <CompleteIcon fontSize="small" />, label: 'Resolution Submitted', color: 'secondary' },
  condition_resolved: { icon: <CompleteIcon fontSize="small" />, label: 'Resolved', color: 'success' },
  condition_withdrawn: { icon: <ClosedIcon fontSize="small" />, label: 'Withdrawn', color: 'default' },
  condition_cancelled: { icon: <ClosedIcon fontSize="small" />, label: 'Cancelled', color: 'default' },
  admin_acted_as: { icon: <HistoryIcon fontSize="small" />, label: 'Entered on behalf of a member', color: 'default' },
};

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getActionConfig(action) {
  // Try exact match first
  if (ACTION_CONFIG[action]) return ACTION_CONFIG[action];
  // Partial match for survey-related actions
  if (action?.includes('survey') || action?.includes('response')) {
    return { icon: <SurveyIcon fontSize="small" />, label: action.replace(/_/g, ' '), color: 'default' };
  }
  return { icon: <HistoryIcon fontSize="small" />, label: action?.replace(/_/g, ' ') || 'Activity', color: 'default' };
}

function getSurveyId(entry) {
  // Extract survey ID from activity log details
  return entry.details?.surveyId || entry.details?.survey_id || null;
}

function getEventDescription(entry) {
  const config = getActionConfig(entry.action);
  const surveyTitle = entry.details?.surveyTitle || entry.details?.title || '';
  const status = entry.details?.newStatus || entry.details?.status || '';

  // OE Condition events: "26-BRA-0007 · Tool crib closes during turnover — L1 Approved"
  if (String(entry.action || '').startsWith('condition_') || entry.action === 'admin_acted_as') {
    const id = entry.details?.conditionId ? `${entry.details.conditionId} · ` : '';
    return `${id}${surveyTitle || 'OE Condition'} — ${config.label}`;
  }

  if (surveyTitle) {
    if (status) return `${surveyTitle} — ${status}`;
    return surveyTitle;
  }
  return config.label;
}

// Internal telemetry actions — never user-facing feed content.
const NOISE_PREFIXES = ['tour_', 'onboarding_', 'aha_'];

export default function SurveyActivityFeed({ maxItems = 10 }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const activeOrg = organizations[0] || null;
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Only events from the ACTIVE ORG's own people (the activity log is a
  // global collection — unfiltered it leaks other orgs' activity), and only
  // real actions (no onboarding/tour telemetry).
  // P4-27: show the member's name (and photo, when they have one) instead of a uid fragment.
  const memberName = (uid) => {
    const m = activeOrg?.membersIndex?.[uid];
    if (m?.displayName) return m.displayName;
    if (uid === user?.uid) return user.displayName || user.email || 'you';
    return `${uid.substring(0, 8)}…`;
  };

  const allowedUids = useMemo(() => {
    if (!activeOrg) return null;
    const uids = new Set(Object.keys(activeOrg.membersIndex || {}));
    (activeOrg.admins || []).forEach((u) => uids.add(u));
    if (user?.uid) uids.add(user.uid);
    return uids;
  }, [activeOrg, user?.uid]);

  useEffect(() => {
    if (!allowedUids) {
      setActivities([]);
      setLoading(false);
      return undefined;
    }
    // Over-fetch, then filter down to the org's members + real actions.
    const q = query(
      collection(db, collections.activityLog),
      where('toolSlug', '==', 'drift-accumulation'),
      orderBy('createdAt', 'desc'),
      limit(80)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const entries = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (e) =>
              allowedUids.has(e.userId) &&
              !NOISE_PREFIXES.some((p) => (e.action || '').startsWith(p))
          )
          .slice(0, maxItems);
        setActivities(entries);
        setLoading(false);
      },
      (error) => {
        console.error('Survey activity feed error:', error.message);
        setActivities([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [maxItems, allowedUids]);

  if (loading) {
    return (
      <Box>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (activities.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No recent activity.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Activity will appear here as surveys are created and responses are submitted.
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {activities.map((entry) => {
        const config = getActionConfig(entry.action);
        const surveyId = getSurveyId(entry);
        const trlId = entry.details?.trlId || null;
        const href = surveyId ? `/drew/surveys/${surveyId}` : trlId ? `/drew/rollup/trl-entry?condition=${trlId}` : null;
        const description = getEventDescription(entry);
        const timeStr = formatTimestamp(entry.createdAt);

        return (
          <ListItem
            key={entry.id}
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              cursor: href ? 'pointer' : 'default',
              '&:hover': href ? { bgcolor: 'action.hover' } : {},
            }}
            onClick={() => {
              if (href) navigate(href);
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: `${config.color}.main` }}>
              {config.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {description}
                  </Typography>
                  <Chip
                    label={config.label}
                    size="small"
                    color={config.color}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {timeStr}
                  {entry.userId && ` · ${memberName(entry.userId)}`}
                </Typography>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
}
